/**
 * SuperAdminShell — the ONE frame every super-admin surface renders in.
 *
 * Before this, the console was two pages that happened to share a sidebar:
 * the tabbed dashboard and the tenant workspace, each with its own header,
 * its own back-button placement and its own scroll behaviour. Moving between
 * them felt like leaving one app for another.
 *
 * The shell fixes the frame and lets the pages fill it:
 *
 *   ┌ console header — never scrolls ──────────────────────────────────┐
 *   │  Super Admin        Tenants · Users · API Keys        [actions]   │
 *   ├ chrome — optional, never scrolls (workspace: Back · tenant · tabs) ┤
 *   ├ body — the ONLY thing that scrolls ───────────────────────────────┤
 *   │  …                                                                 │
 *   └────────────────────────────────────────────────────────────────────┘
 *
 * Layout is a flex column with one overflow region, not position:sticky —
 * sticky headers inside a scrolling main area jitter and depend on every
 * ancestor's overflow; a bounded column with a single scroll body does not.
 * The shell owns every layout rule it needs (.sa-console-*), so it does not
 * depend on AdminPage.css being loaded for `.main-content` to behave.
 */

import { useNavigate } from 'react-router-dom';
import { Sidebar } from '@/components/layout/Sidebar';
import { useLanguage } from '@/hooks/useLanguage';
import { useTenantPath } from '@/routing/paths';
import './console.css';
import './console-shell.css';

export type ConsoleNav = 'tenants' | 'users' | 'api-keys';

interface Props {
  /** Which primary section is active — drives the highlighted nav item. */
  active: ConsoleNav;
  /** A fixed block under the header and above the scroll body. The tenant
   *  workspace puts Back + tenant context + section tabs here so they stay
   *  put while the content scrolls. */
  chrome?: React.ReactNode;
  /** Right-aligned controls in the header row (e.g. "Switch tenant"). */
  headerActions?: React.ReactNode;
  /** The body does not scroll: its one child fills it and scrolls its own
   *  columns (the White-Label workspace). The header is then ONE slim line —
   *  the console's name at the start of its tabs — so the workspace gets the
   *  height the big title block took. On phones the whole console scrolls
   *  instead, so the fixed header does not eat the screen. */
  fill?: boolean;
  children: React.ReactNode;
}

export default function SuperAdminShell({ active, chrome, headerActions, fill = false, children }: Props) {
  const navigate = useNavigate();
  const tenantPath = useTenantPath();
  const { t } = useLanguage();

  const items: Array<{ id: ConsoleNav; label: string; to: string }> = [
    { id: 'tenants', label: t('superAdmin.console.navTenants'), to: '/super-admin' },
    { id: 'users', label: t('superAdmin.console.navUsers'), to: '/super-admin?tab=users' },
    { id: 'api-keys', label: t('superAdmin.console.navApiKeys'), to: '/super-admin?tab=api-keys' },
  ];

  const nav = (
    <nav className="admin-tabs sa-console-tabs" aria-label={t('superAdmin.console.navLabel')}>
      {items.map(item => (
        <button
          key={item.id}
          type="button"
          className={`tab-btn ${active === item.id ? 'active' : ''}`}
          aria-current={active === item.id ? 'page' : undefined}
          onClick={() => navigate(tenantPath(item.to))}
          data-testid={`console-nav-${item.id}`}
        >
          {item.label}
        </button>
      ))}
      {headerActions && <div className="sa-console-tabs-actions">{headerActions}</div>}
    </nav>
  );

  return (
    <div className="super-admin-container sa-console-root">
      <Sidebar
        options={{
          showHome: true,
          showChat: false,
          showBots: true,
          showSearch: true,
          showAdmin: false,
          showSuperAdmin: true,
          showConversations: false,
          showBotsList: false,
          activeItem: 'super-admin',
        }}
      />
      <div className="main-container sa-console-main">
        <main className={`main-content sa-console${fill ? ' sa-console--fill' : ''}`} data-testid="super-admin-console">
          {/* The frame is the Admin page's frame, class for class: a centered
              title block (.simple-chat-header) above a full-width tab strip
              (.admin-tabs / .tab-btn). Both carry flex-shrink:0, so inside
              this flex column they stay put while only the body scrolls.
              Styles come from styles/page-frame.css, loaded globally. */}
          {fill ? (
            /* The Branding workspace: one slim line — the console's name, then
               its tabs — instead of the big title block above the tabs. */
            <div className="sa-console-compact-head" data-testid="super-admin-compact-head">
              <h1 className="sa-console-compact-title">{t('superAdmin.console.title')}</h1>
              {nav}
            </div>
          ) : (<>
            <header className="simple-chat-header">
              <h1 className="bot-title">{t('superAdmin.console.title')}</h1>
              <p className="text-muted">{t('superAdmin.console.subtitle')}</p>
            </header>
            {nav}
          </>)}

          {chrome && <div className="sa-console-chrome">{chrome}</div>}

          <div className={`sa-console-body${fill ? ' sa-console-body--fill' : ''}`} data-testid="super-admin-console-body">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
